import { WebMercatorViewport } from "@deck.gl/core";
import { chooseParkingLabels, type LabelBox } from "./parkingLabels";
import { GeoJsonLayer, IconLayer, PathLayer, TextLayer, SolidPolygonLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { ScatterplotLayer } from "@deck.gl/layers";
import type { Layer } from "@deck.gl/core";
import type { Feature, FeatureCollection, Geometry, Polygon, MultiPolygon, LineString, Point } from "geojson";
import type { CampusFloodStation, IncidentFeature, CampusZoneProperties } from "@chula/shared";

export interface CctvCamera {
  hlsUrl?: string;
  id: string;
  name: string;
  lat: number;
  lng: number;
  vendor: string;
  imageUrl?: string;
  /** Metres from campus centre — the panel shows "380 m" rather than making the operator judge it. */
  distanceM?: number;
  organization?: string;
}
export interface ShuttleVehicle {
  id: string;
  line: string;
  lat: number;
  lng: number;
  bearing?: number;
  occupancy?: string;
  speedKmh?: number;
  atStop?: string;
  nextStop?: string;
}

export interface RouteProps { route: string; color: string; label: string }
export interface StopProps { id: string; name: string; lines: string[] }
export interface StationProps { id: string; name: string; system: "BTS" | "MRT"; line: string; code: string }
export interface TransitLineProps { id: string; system: "BTS" | "MRT" | "ART"; line: string; color: string; ref: string }
export interface GateProps { id: string; kind: "gate" | "lift-gate" | "entrance"; name: string | null; nameTh: string | null; named: boolean }
export interface NeighborhoodBuildingProps { id: string; name: string | null; nameEn: string | null; height: number; levels: number | null; building: string }

const ZONE_COLORS: Record<string, [number, number, number]> = {
  academic: [56, 189, 248],
  residential: [167, 139, 250],
  athletic: [31, 110, 67],
  park: [52, 211, 153],
  commercial: [183, 48, 122],
  service: [122, 132, 151],
  perimeter: [183, 48, 122],
};

type ZoneFeature = Feature<Polygon | MultiPolygon, CampusZoneProperties>;

export function campusBoundaryLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>,
  options: { extruded?: boolean } = {},
) {
  return new GeoJsonLayer({
    id: "campus-boundary",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    extruded: options.extruded ?? false,
    getFillColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [120, 120, 120];
      return [z[0], z[1], z[2], 38] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [200, 200, 200];
      return [z[0], z[1], z[2], 200] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1,
    getElevation: ((f: ZoneFeature) => f.properties.height ?? 0) as unknown as number,
  });
}

const INCIDENT_COLORS: Record<IncidentFeature["category"], [number, number, number]> = {
  "traffic-accident": [239, 68, 68],
  "traffic-congestion": [162, 58, 38],
  construction: [162, 58, 38],
  flooding: [56, 189, 248],
  waste: [168, 162, 158],
  lighting: [253, 224, 71],
  sidewalk: [167, 139, 250],
  drainage: [125, 211, 252],
  trees: [52, 211, 153],
  other: [148, 163, 184],
};

export function incidentLayer(id: string, data: IncidentFeature[]) {
  return new ScatterplotLayer<IncidentFeature>({
    id,
    data,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => {
      // Telegram / concierge reports (reporterPlatform === "internal") get a
      // hot-magenta fill so they stand out from Traffy / iTIC / city-reporter
      // pins. A citizen who just filed a report must be able to find their
      // pin instantly — same colour as every "other" category pin is a
      // usability failure.
      if (d.reporterPlatform === "internal") {
        return d.status === "resolved" ? [236, 72, 153, 110] : [236, 72, 153, 240];
      }
      const c = INCIDENT_COLORS[d.category] ?? [200, 200, 200];
      return [...c, d.status === "resolved" ? 90 : 220] as [number, number, number, number];
    },
    getRadius: (d) => {
      // Telegram / concierge reports are always large enough to find on a
      // crowded campus map — never smaller than the medium tier.
      if (d.reporterPlatform === "internal") {
        return d.status === "resolved" ? 16 : 22;
      }
      return d.severity === "high" ? 26 : d.severity === "medium" ? 18 : 12;
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 28,
    stroked: true,
    getLineColor: (d) => {
      // Bright white ring around internal pins so they pop against the
      // pink campus tint. Dark ring for everything else.
      if (d.reporterPlatform === "internal") {
        return d.status === "resolved" ? [255, 255, 255, 150] : [255, 255, 255, 245];
      }
      return [10, 14, 20, 230];
    },
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 3,
    pickable: true,
  });
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return [200, 200, 200];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function shuttleRoutesLayer(collection: FeatureCollection<LineString, RouteProps>) {
  return new GeoJsonLayer({
    id: "cu-shuttle-routes",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: ((f: Feature<LineString, RouteProps>) => {
      const c = hexToRgb(f.properties.color);
      return [c[0], c[1], c[2], 220] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 3,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 5,
  });
}

/**
 * Render a single CU shuttle route (one line of the 5). Per-line toggles let
 * the user isolate a route to read it; the colour comes from the route's own
 * `color` property (matches PMCU's printed legend).
 */
export function shuttleRouteLineLayer(
  routeId: string,
  collection: FeatureCollection<LineString, RouteProps>,
) {
  const feature = collection.features.find((f) => f.properties.route === routeId);
  if (!feature) return null;
  const rgb = hexToRgb(feature.properties.color);
  return new GeoJsonLayer({
    id: `cu-shuttle-line-${routeId}`,
    data: { type: "FeatureCollection", features: [feature] } as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: [rgb[0], rgb[1], rgb[2], 230],
    getLineWidth: 4,
    lineWidthMinPixels: 3,
    lineWidthMaxPixels: 6,
  });
}

export function shuttleStopsLayer(collection: FeatureCollection<Point, StopProps>) {
  return new ScatterplotLayer({
    id: "cu-shuttle-stops",
    data: collection.features,
    getPosition: ((f: Feature<Point, StopProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 18,
    radiusMinPixels: 3,
    radiusMaxPixels: 7,
    getFillColor: [31, 110, 67, 230],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
  });
}

// Bus glyph — a plan-view coach (rounded body, windscreen band, wheel arches)
// drawn pointing UP, so IconLayer's getAngle can rotate it to the bus's
// compass bearing. One SVG per line colour, built once and cached; deck.gl
// takes the data URI straight as the icon url.
// Raster size of the glyph (drawn in a 64-unit design space, scaled ×2 so it
// stays crisp at the on-screen sizes below).
const BUS_ICON_SIZE = 128;
const BUS_ICON_DESIGN_UNITS = 64;
// On-screen size in CSS px. A bus must read as a bus at campus zoom, not as a
// dot among the parking labels — hence well above the 26 px it started at.
const BUS_SIZE_PX = 48;
const BUS_SIZE_MIN_PX = 32;
const BUS_SIZE_MAX_PX = 72;

const SHUTTLE_LINE_COLOR: Record<string, string> = {
  "1": "#ef4444", "2": "#38bdf8", "3": "#34d399", "4": "#f59e0b", "5": "#a78bfa",
};
const BUS_ICON_FALLBACK_KEY = "x";
const BUS_ICON_KEYS = [...Object.keys(SHUTTLE_LINE_COLOR), BUS_ICON_FALLBACK_KEY];

/** Top-down bus glyph: body, dark windscreen band, pale roof, four wheels. */
function drawBus(ctx: CanvasRenderingContext2D, ox: number, fill: string) {
  const rr = (x: number, y: number, w: number, h: number, r: number, color: string, alpha = 1) => {
    ctx.globalAlpha = alpha; ctx.fillStyle = color;
    ctx.beginPath(); ctx.roundRect(ox + x, y, w, h, r); ctx.fill();
  };
  ctx.globalAlpha = 1; ctx.lineWidth = 3; ctx.strokeStyle = "#0a0e14"; ctx.fillStyle = fill;
  ctx.beginPath(); ctx.roundRect(ox + 18, 6, 28, 52, 6); ctx.fill(); ctx.stroke();
  rr(21, 10, 22, 9, 2, "#0a0e14", 0.85);
  rr(21, 23, 22, 26, 2, "#ffffff", 0.28);
  for (const [x, y] of [[14, 14], [45, 14], [14, 40], [45, 40]]) rr(x, y, 5, 10, 2, "#0a0e14");
  ctx.globalAlpha = 1;
}

const BUS_ICON_URL_PREFIX = "cu-bus://line-";
let busPngCache: Map<string, Uint8Array> | null | undefined;

/**
 * One 64 px PNG per line colour, rasterised once on a canvas and kept as
 * bytes. deck's IconLayer loads every `getIcon.url` through fetch(), which the
 * page CSP (connect-src) blocks for `data:` URLs — the fleet silently drew
 * nothing. Serving the bytes through `loadOptions.fetch` never touches the
 * network or the CSP. Returns null where no 2D canvas exists (jsdom / SSR);
 * callers then fall back to the SVG data-URI form.
 */
function busPngs(): Map<string, Uint8Array> | null {
  if (busPngCache !== undefined) return busPngCache;
  if (typeof document === "undefined") return (busPngCache = null);
  const canvas = document.createElement("canvas");
  canvas.width = BUS_ICON_SIZE; canvas.height = BUS_ICON_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx || typeof ctx.roundRect !== "function") return (busPngCache = null);
  ctx.scale(BUS_ICON_SIZE / BUS_ICON_DESIGN_UNITS, BUS_ICON_SIZE / BUS_ICON_DESIGN_UNITS);
  const out = new Map<string, Uint8Array>();
  for (const key of BUS_ICON_KEYS) {
    ctx.clearRect(0, 0, BUS_ICON_DESIGN_UNITS, BUS_ICON_DESIGN_UNITS);
    drawBus(ctx, 0, SHUTTLE_LINE_COLOR[key] ?? "#22d3ee");
    const b64 = canvas.toDataURL("image/png").split(",")[1] ?? "";
    out.set(BUS_ICON_URL_PREFIX + key, Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
  }
  return (busPngCache = out);
}

/** loaders.gl fetch hook: answer the synthetic bus icon URLs from memory. */
function busIconFetch(pngs: Map<string, Uint8Array>) {
  return (url: string): Promise<Response> => {
    const bytes = pngs.get(url);
    return Promise.resolve(
      bytes
        ? new Response(bytes as BodyInit, { status: 200, headers: { "content-type": "image/png" } })
        : new Response(null, { status: 404 }),
    );
  };
}

const busIconCache = new Map<string, string>();
/** SVG data-URI fallback for environments without canvas (tests). */
function busIconUrl(fill: string): string {
  const hit = busIconCache.get(fill);
  if (hit) return hit;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${BUS_ICON_SIZE}" height="${BUS_ICON_SIZE}">` +
    `<rect x="18" y="6" width="28" height="52" rx="6" fill="${fill}" stroke="#0a0e14" stroke-width="3"/>` +
    `<rect x="21" y="10" width="22" height="9" rx="2" fill="#0a0e14" opacity="0.85"/>` +
    `<rect x="21" y="23" width="22" height="26" rx="2" fill="#ffffff" opacity="0.28"/>` +
    `<rect x="14" y="14" width="5" height="10" rx="2" fill="#0a0e14"/><rect x="45" y="14" width="5" height="10" rx="2" fill="#0a0e14"/>` +
    `<rect x="14" y="40" width="5" height="10" rx="2" fill="#0a0e14"/><rect x="45" y="40" width="5" height="10" rx="2" fill="#0a0e14"/>` +
    `</svg>`;
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  busIconCache.set(fill, url);
  return url;
}

/**
 * Fleet as bus-shaped icons, rotated to heading, coloured by line. The data
 * is whatever useShuttlePositions hands over — live GPS when a token exists,
 * the clock-driven simulator otherwise — and the layer cannot tell the
 * difference; the tier is surfaced by the palette row and the BUS chip.
 */
export function shuttleVehiclesLayer(vehicles: ShuttleVehicle[]) {
  const pngs = busPngs();
  const iconOf = (url: string) => ({ url, width: BUS_ICON_SIZE, height: BUS_ICON_SIZE, anchorX: BUS_ICON_SIZE / 2, anchorY: BUS_ICON_SIZE / 2 });
  const iconProps = pngs
    ? {
        loadOptions: { fetch: busIconFetch(pngs) },
        getIcon: (v: ShuttleVehicle) => iconOf(BUS_ICON_URL_PREFIX + (v.line in SHUTTLE_LINE_COLOR ? v.line : BUS_ICON_FALLBACK_KEY)),
      }
    : {
        getIcon: (v: ShuttleVehicle) => ({
          url: busIconUrl(SHUTTLE_LINE_COLOR[v.line] ?? "#22d3ee"),
          width: BUS_ICON_SIZE,
          height: BUS_ICON_SIZE,
          anchorX: BUS_ICON_SIZE / 2,
          anchorY: BUS_ICON_SIZE / 2,
        }),
      };
  return new IconLayer<ShuttleVehicle>({
    id: "cu-shuttle-vehicles",
    data: vehicles,
    getPosition: (v) => [v.lng, v.lat],
    ...iconProps,
    getAngle: (v) => -(v.bearing ?? 0), // deck.gl rotates counter-clockwise; bearing is clockwise from north
    getSize: BUS_SIZE_PX,
    sizeUnits: "pixels",
    sizeMinPixels: BUS_SIZE_MIN_PX,
    sizeMaxPixels: BUS_SIZE_MAX_PX,
    billboard: false,
    pickable: true,
    updateTriggers: { getPosition: vehicles, getAngle: vehicles },
  });
}

export function transitStationsLayer(collection: FeatureCollection<Point, StationProps>) {
  return new ScatterplotLayer({
    id: "transit-stations",
    data: collection.features,
    getPosition: ((f: Feature<Point, StationProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 32,
    radiusMinPixels: 5,
    radiusMaxPixels: 10,
    getFillColor: ((f: Feature<Point, StationProps>) =>
      f.properties.system === "BTS"
        ? [56, 189, 248, 240]
        : [96, 165, 250, 240]) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

export interface CuLandProperties {
  id: string;
  name: { en: string; th: string; zh: string };
  kind: "commercial" | "mixed-use" | "athletic" | "park" | "residential" | "healthcare" | "cultural" | "education";
  operator: string;
  color: string;
  describe: string;
}


// ─── Campus building footprints (real OSM data) ──────────────────────────
export interface BuildingProperties {
  id: string;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  building: string;
  levels: number | null;
  height: number | null;
  operator: string | null;
}

function buildingHeightMeters(props: BuildingProperties): number {
  if (props.height) return props.height;
  if (props.levels) return props.levels * 3;
  return 9;
}

/**
 * Render every Chula building as a filled, tappable polygon.
 * In 2D: low-key footprint outlines (named buildings warmer).
 * In 3D: same polygons extruded to their `height`, with a height-graded
 *        fill so the skyline reads at a glance.
 * In 3DS (substructure): same extrusion at much lower opacity so the
 *        underground utilities below show through — SimCity-2000-style
 *        cutaway. Strokes stay visible at full opacity for context.
 */
/**
 * Map keyed by building id (`way/…`) → instantaneous load + anomaly score.
 * When present, buildings color by load intensity / anomaly flag instead
 * of by height bands. Provided via `options.energyByBuildingId`.
 */
export type EnergyReading = { currentKw: number; vsAvg: number; anomalyScore: number };

export function buildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, BuildingProperties>,
  options: {
    extruded?: boolean;
    ghosted?: boolean;
    energyByBuildingId?: Map<string, EnergyReading>;
    ghostConsumptionMode?: boolean;
    /** Aerial base map: fade the fill so the imagery stays readable. */
    dimFill?: boolean;
  } = {},
) {
  const extruded = options.extruded ?? false;
  const ghosted = options.ghosted ?? false;
  const energyMap = options.energyByBuildingId;
  const energyMode = !!energyMap;
  const ghostConsumptionMode = options.ghostConsumptionMode ?? false;
  const dimFill = (options.dimFill ?? false) && !ghosted && !energyMode;

  const fillAlpha = ghosted ? 32 : 1.0;
  const lineAlpha = ghosted ? 110 : 220;

  // Height ramp — 4 bands of magenta → amber, named buildings sit on the warmer
  // half so the campus reads warmer than the surrounding city.
  const heightColor = (h: number, named: boolean): [number, number, number, number] => {
    if (extruded) {
      if (h >= 50) return [240, 200, 130, ghosted ? fillAlpha : 235];
      if (h >= 30) return [220, 130, 150, ghosted ? fillAlpha : 220];
      if (h >= 15) return [180, 70, 130,  ghosted ? fillAlpha : 205];
      return       [120, 60, 110,  ghosted ? fillAlpha : 195];
    }
    if (dimFill) {
      // Over satellite photos the footprints become outlines-with-a-hint —
      // still hoverable/clickable, no longer a paint bucket over the truth.
      return named
        ? [183, 48, 122, 30]
        : [148, 163, 184, 18];
    }
    return named
      ? [183, 48, 122, ghosted ? fillAlpha : 110]
      : [148, 163, 184, ghosted ? fillAlpha : 80];
  };

  /**
   * Energy ramp — viridis-ish: cool teal (low) → green → yellow → red
   * (anomalous). Anomaly score ≥0.7 overrides to pure red regardless of
   * absolute kW, since a "stuck AC" small building still needs flagging.
   */
  const energyColor = (kw: number, anomaly: number): [number, number, number, number] => {
    if (anomaly >= 0.7) return [240, 60, 60, ghosted ? fillAlpha * 4 : 240];
    // Map kW to 0..1 with a soft knee — 0 kW dark, 200 kW bright.
    const t = Math.min(1, Math.log10(kw + 1) / Math.log10(201));
    if (t < 0.2)  return [40, 90, 110,   ghosted ? fillAlpha : 200];
    if (t < 0.4)  return [50, 140, 110,  ghosted ? fillAlpha : 215];
    if (t < 0.6)  return [120, 175, 90,  ghosted ? fillAlpha : 225];
    if (t < 0.8)  return [220, 200, 80,  ghosted ? fillAlpha : 235];
    return            [240, 140, 60,  ghosted ? fillAlpha : 240];
  };

  const colorFor = (
    f: Feature<Polygon | MultiPolygon, BuildingProperties>,
  ): [number, number, number, number] => {
    if (ghostConsumptionMode) {
      const id = f.properties.id ?? "";
      const e = energyMap?.get(id);
      if (e && e.anomalyScore >= 0.7) {
        return [255, 30, 30, 255]; // Blazing red for ghost consumers
      }
      return [30, 40, 50, 15]; // Barely visible for non-anomalies
    }
    if (energyMode) {
      const id = f.properties.id ?? "";
      const e = energyMap!.get(id);
      if (e) return energyColor(e.currentKw, e.anomalyScore);
      // No reading → faint
      return [70, 90, 110, ghosted ? fillAlpha : 150];
    }
    return heightColor(buildingHeightMeters(f.properties), !!f.properties.name);
  };

  return new GeoJsonLayer({
    id: "campus-buildings",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    extruded,
    material: extruded && !ghosted
      ? { ambient: 0.6, diffuse: 0.7, shininess: 12, specularColor: [255, 220, 180] }
      : false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      colorFor(f)) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) => {
      if (ghostConsumptionMode) {
        const e = energyMap?.get(f.properties.id ?? "");
        if (e && e.anomalyScore >= 0.7) return [255, 80, 80, 255] as [number, number, number, number];
        return [40, 50, 60, 30] as [number, number, number, number];
      }
      if (energyMode) {
        const e = energyMap!.get(f.properties.id ?? "");
        if (e && e.anomalyScore >= 0.7) return [255, 80, 80, 255] as [number, number, number, number];
        return [80, 130, 150, lineAlpha] as [number, number, number, number];
      }
      return f.properties.name
        ? ([183, 48, 122, lineAlpha] as [number, number, number, number])
        : ([148, 163, 184, lineAlpha] as [number, number, number, number]);
    }) as unknown as [number, number, number, number],
    getLineWidth: 0.8,
    lineWidthMinPixels: 0.5,
    getElevation: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      buildingHeightMeters(f.properties)) as unknown as number,
    opacity: ghosted ? 0.35 : 1,
    updateTriggers: {
      getFillColor: [extruded, ghosted, energyMode, energyMap, ghostConsumptionMode, dimFill],
      getLineColor: [ghosted, energyMode, energyMap, ghostConsumptionMode],
    },
  });
}

// ─── Underground / substructure PathLayer factories ─────────────────────
// Re-render utility line geometry at a negative z so we can see the
// network "buried" beneath the ghosted buildings. Depths are typical Bangkok
// burial: electricity ~2 m, water ~3 m, drainage ~4 m.

function lineFeaturesAt(
  collection: FeatureCollection,
  depthMeters: number,
): Array<{ path: [number, number, number][]; properties: Record<string, unknown> }> {
  const out: Array<{ path: [number, number, number][]; properties: Record<string, unknown> }> = [];
  for (const f of collection.features) {
    if (f.geometry.type !== "LineString") continue;
    const coords = (f.geometry.coordinates as [number, number][]).map(
      (c) => [c[0], c[1], -depthMeters] as [number, number, number],
    );
    out.push({ path: coords, properties: (f.properties ?? {}) as Record<string, unknown> });
  }
  return out;
}

// ── Underground path rendering ──────────────────────────────────────────
// Sizes are deliberately exaggerated 2-4× real-world diameter so the network
// reads at a glance. widthUnits = "meters" means widths scale with zoom and
// look like actual tubes, not pixel ribbons. A halo PathLayer renders behind
// each tube at 1.6× width / 40% alpha to give the cables/pipes a soft glow
// against the ghosted buildings above.
//
// Labels prefix the underground depth (e.g. "▼2m · Pathumwan 115 kV") and
// drop down by another 0.5 m so they read as anchored to the buried feature,
// not floating at street level. depthTest stays off so labels show through
// the ghosted buildings — that's the point of 3DS mode.

interface UndergroundLabelDatum {
  position: [number, number, number];
  text: string;
}
function midPointAt(path: [number, number, number][], depthDrop: number): [number, number, number] {
  const mid = Math.floor(path.length / 2);
  const p = path[mid];
  return [p[0], p[1], p[2] - depthDrop];
}

export function electricityPathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 2);
  // SimCity-clean: single hairline per network, no halo. Pixel widths stay
  // consistent across zoom so the schematic reads at any angle.
  const labels: UndergroundLabelDatum[] = paths
    .filter((d) => (d.properties as unknown as ElectricityProps).kind === "hv-backbone" || (d.properties as unknown as ElectricityProps).name)
    .map((d) => {
      const p = d.properties as unknown as ElectricityProps;
      const name = p.name || (p.kind === "hv-backbone" ? "HV 115 kV" : "MV 22 kV");
      return { position: midPointAt(d.path, 0.5), text: `▼2m · ${name}` };
    });
  return [
    new PathLayer({
      id: "cu-electricity-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as ElectricityProps).kind === "hv-backbone"
          ? [31, 110, 67, 255]
          : [82, 158, 118, 220],
      getWidth: (d) => (d.properties as ElectricityProps).kind === "hv-backbone" ? 3 : 2,
      widthUnits: "pixels",
      widthMinPixels: 2,
      widthMaxPixels: 5,
      pickable: true,
    }),
    new TextLayer<UndergroundLabelDatum>({
      id: "cu-electricity-labels-3ds",
      data: labels,
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getSize: 11,
      getColor: [253, 246, 227, 255],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: '"Jost", "Prompt", sans-serif',
      fontWeight: "500",
      background: true,
      backgroundPadding: [3, 2, 3, 2],
      getBackgroundColor: [12, 10, 6, 210],
      parameters: { depthCompare: "always", depthWriteEnabled: false },
      pickable: false,
    }),
  ];
}

export function waterPathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 3);
  const labels: UndergroundLabelDatum[] = paths
    .filter((d) => (d.properties as unknown as WaterProps).kind === "main" || (d.properties as unknown as WaterProps).name)
    .map((d) => {
      const p = d.properties as unknown as WaterProps;
      const parts: string[] = [];
      if (p.name) parts.push(p.name);
      else parts.push(p.kind === "main" ? "Water main" : "Lateral");
      if (p.diameter) parts.push(`Ø${p.diameter}`);
      return { position: midPointAt(d.path, 0.5), text: `▼3m · ${parts.join(" ")}` };
    });
  return [
    new PathLayer({
      id: "cu-water-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as WaterProps).kind === "main"
          ? [56, 189, 248, 255]
          : [147, 197, 253, 210],
      getWidth: (d) => (d.properties as WaterProps).kind === "main" ? 3 : 2,
      widthUnits: "pixels",
      widthMinPixels: 2,
      widthMaxPixels: 5,
      pickable: true,
    }),
    new TextLayer<UndergroundLabelDatum>({
      id: "cu-water-labels-3ds",
      data: labels,
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getSize: 11,
      getColor: [253, 246, 227, 255],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: '"Jost", "Prompt", sans-serif',
      fontWeight: "500",
      background: true,
      backgroundPadding: [3, 2, 3, 2],
      getBackgroundColor: [6, 12, 18, 210],
      parameters: { depthCompare: "always", depthWriteEnabled: false },
      pickable: false,
    }),
  ];
}

export function drainagePathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 4);
  const labels: UndergroundLabelDatum[] = paths
    .filter((d) => (d.properties as unknown as DrainageProps).kind === "main" || (d.properties as unknown as DrainageProps).name)
    .map((d) => {
      const p = d.properties as unknown as DrainageProps;
      const parts: string[] = [];
      if (p.name) parts.push(p.name);
      else parts.push(p.kind === "main" ? "Storm main" : "Storm lateral");
      if (p.diameter) parts.push(`Ø${p.diameter}`);
      return { position: midPointAt(d.path, 0.5), text: `▼4m · ${parts.join(" ")}` };
    });
  return [
    new PathLayer({
      id: "cu-drainage-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as DrainageProps).kind === "main"
          ? [16, 185, 129, 255]
          : [110, 231, 183, 210],
      getWidth: (d) => (d.properties as DrainageProps).kind === "main" ? 3 : 2,
      widthUnits: "pixels",
      widthMinPixels: 2,
      widthMaxPixels: 5,
      pickable: true,
    }),
    new TextLayer<UndergroundLabelDatum>({
      id: "cu-drainage-labels-3ds",
      data: labels,
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getSize: 11,
      getColor: [253, 246, 227, 255],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: '"Jost", "Prompt", sans-serif',
      fontWeight: "500",
      background: true,
      backgroundPadding: [3, 2, 3, 2],
      getBackgroundColor: [6, 18, 14, 210],
      parameters: { depthCompare: "always", depthWriteEnabled: false },
      pickable: false,
    }),
  ];
}

export interface BmaPoi {
  id: string;
  kind:
    | "hospital"
    | "health-center"
    | "school"
    | "fire-station"
    | "police-station"
    | "park"
    | "market"
    | "bma-office"
    | "flood-gate"
    | "pump-station"
    | "cctv"
    | "bus-stop"
    | "other";
  name: string;
  lat: number;
  lng: number;
  description?: string;
}

const POI_COLORS: Record<BmaPoi["kind"], [number, number, number]> = {
  hospital:        [239, 68, 68],
  "health-center": [248, 113, 113],
  school:          [56, 189, 248],
  "fire-station":  [251, 146, 60],
  "police-station":[59, 130, 246],
  park:            [52, 211, 153],
  market:          [108, 99, 92],
  "bma-office":    [168, 162, 158],
  "flood-gate":    [34, 211, 238],
  "pump-station":  [125, 211, 252],
  cctv:            [229, 231, 235],
  "bus-stop":      [167, 139, 250],
  other:           [148, 163, 184],
};


// ─── Trees ──────────────────────────────────────────────────────────────
// CU's documented trees — placeholder is the OSM `natural=tree` set
// (4,107 trees inside the campus bbox). Renders each tree as a canopy
// disk sized by its OSM crown radius (default 4.5 m where unknown).
// Layered above the wood polygons. Click → species / height / id.

export interface TreeProps {
  id: string;
  name?: string | null;
  species?: string | null;
  speciesTh?: string | null;
  genus?: string | null;
  taxon?: string | null;
  leafType?: string | null;
  leafCycle?: string | null;
  heightM?: number | null;
  crownRadiusM?: number | null;
  circumferenceM?: string | null;
  denotation?: string | null;
}

/**
 * Wood / grove polygons — wider canopy area context behind individual
 * tree dots. Renders with a soft green fill so individual trees still
 * read against it.
 */
export function woodPolygonsLayer(collection: FeatureCollection<Polygon, { id: string; name?: string | null }>) {
  return new GeoJsonLayer({
    id: "campus-wood",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: false,
    getFillColor: [42, 152, 89, 60],
    getLineColor: [42, 152, 89, 140],
    getLineWidth: 0.8,
    lineWidthMinPixels: 0.5,
  });
}

/**
 * Individual trees — ScatterplotLayer with crown radius in meters,
 * deterministic green shade per tree id so the canopy reads as a
 * forest rather than a flat fill.
 */
export function campusTreesLayer(
  collection: FeatureCollection<Point, TreeProps>,
  options: { extruded?: boolean } = {},
) {
  const extruded = options.extruded ?? false;
  // Lightweight per-id noise so different trees get slightly different
  // greens without storing state. Mulberry32-ish, inline.
  const greenFor = (id: string): [number, number, number, number] => {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
      h = Math.imul(h ^ id.charCodeAt(i), 16777619);
    }
    h += h << 13; h ^= h >>> 7;
    h += h << 3;  h ^= h >>> 17;
    h += h << 5;
    const v = (h >>> 0) / 4294967295;
    // Base #2A9859, jitter ± 20 on each channel
    const r = Math.round(42 + (v - 0.5) * 40);
    const g = Math.round(152 + (v * 31) % 30);
    const b = Math.round(89 + (v - 0.5) * 20);
    return [Math.max(20, r), Math.max(120, g), Math.max(60, b), 220];
  };
  return new ScatterplotLayer<Feature<Point, TreeProps>>({
    id: "campus-trees",
    data: collection.features,
    pickable: true,
    stroked: true,
    filled: true,
    // Trees are campus-scale only — 4,107 features with per-id BVH
    // maintenance is the single largest CPU cost on every pan/zoom.
    // Below z15 the BKKx fabric dominates visually; above z16 the
    // operator is at building scale and the trees matter.
    // The deck.gl TS types don't expose minZoom/maxZoom on
    // ScatterplotLayer directly; cast through LayerProps.
    ...({
      minZoom: 16,
      maxZoom: 22,
    } as Record<string, unknown>),
    radiusUnits: "meters",
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getRadius: (f) => f.properties.crownRadiusM ?? 4.5,
    getFillColor: (f) => greenFor(f.properties.id),
    getLineColor: [20, 80, 40, 180],
    lineWidthMinPixels: 0.3,
    radiusMinPixels: 2,
    radiusMaxPixels: 32,
    // In 3D, give trees a touch of vertical so they read as canopy domes,
    // not flat coins. Capped to keep them under buildings.
    extensions: extruded ? [] : [],
  });
}

export interface AqStation {
  id: string;
  name: string;
  address: string;
  pm25: number | null;
  pm10: number | null;
  lat: number;
  lng: number;
}

// ── Neighborhood POIs (OSM-derived, 11 categories) ──────────────────
// One layer per category so the LayerPalette can toggle each independently.
// Colors picked to read against both dark and aerial base maps.
export interface NeighborhoodPoi {
  id: string;
  category:
    | "school" | "religious" | "hotel" | "pharmacy" | "cultural"
    | "bus_stop" | "pier" | "embassy" | "ev" | "retail" | "bank";
  subtype: string;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  lng: number;
  lat: number;
  website: string | null;
  phone: string | null;
  operator: string | null;
}

const NEIGHBORHOOD_POI_STYLE: Record<NeighborhoodPoi["category"], {
  fill: [number, number, number];
  radius: number;
  radiusMin: number;
  radiusMax: number;
}> = {
  school:    { fill: [ 96, 165, 250], radius: 60, radiusMin: 5, radiusMax: 14 },
  religious: { fill: [108, 99, 92], radius: 50, radiusMin: 4, radiusMax: 12 },
  hotel:     { fill: [192, 132, 252], radius: 32, radiusMin: 3, radiusMax: 10 },
  pharmacy:  { fill: [ 52, 211, 153], radius: 38, radiusMin: 4, radiusMax: 11 },
  cultural:  { fill: [244, 114, 182], radius: 56, radiusMin: 5, radiusMax: 13 },
  bus_stop:  { fill: [163, 230, 53],  radius: 18, radiusMin: 2, radiusMax: 6  },
  pier:      { fill: [ 56, 189, 248], radius: 50, radiusMin: 5, radiusMax: 12 },
  embassy:   { fill: [239,  68,  68], radius: 60, radiusMin: 6, radiusMax: 14 },
  ev:        { fill: [ 16, 185, 129], radius: 36, radiusMin: 4, radiusMax: 11 },
  retail:    { fill: [251, 146,  60], radius: 24, radiusMin: 3, radiusMax: 8  },
  bank:      { fill: [108, 99, 92], radius: 22, radiusMin: 3, radiusMax: 8  },
};


export function bmaAqStationsLayer(stations: AqStation[]) {
  return new ScatterplotLayer<AqStation>({
    id: "bma-aq-stations",
    data: stations,
    getPosition: (s) => [s.lng, s.lat],
    getRadius: 60,
    radiusMinPixels: 8,
    radiusMaxPixels: 16,
    getFillColor: (s) => {
      const v = s.pm25 ?? 0;
      if (v < 12) return [34, 197, 94, 255];
      if (v < 35) return [250, 204, 21, 255];
      if (v < 55) return [249, 115, 22, 255];
      if (v < 150) return [239, 68, 68, 255];
      return [127, 29, 29, 255];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
  });
}

// ── IQAir-style AQI heatmap ──────────────────────────────────────────
// Multi-station PM2.5 surface coloured by the US EPA AQI bands that
// IQAir uses globally:
//   0–50    Good            #00E400
//   51–100  Moderate        #FFFF00
//   101–150 USG             #FF7E00
//   151–200 Unhealthy       #FF0000
//   201–300 Very Unhealthy  #8F3F97
//   301+    Hazardous       #7E0023
//
// The HeatmapLayer's `colorRange` quantizes weights into 6 bins, so we
// pass the EPA bands at matching positions. `getWeight` uses AQI when
// present (already normalized 0–500), otherwise a PM2.5 → AQI rough
// approximation so we still render when only PM2.5 is available.
export interface AqHeatmapPoint {
  lat: number;
  lng: number;
  pm25: number | null;
  aqi: number | null;
}

function pm25ToAqi(pm: number): number {
  // EPA piecewise linear breakpoints (PM2.5 24h, μg/m³ → US AQI).
  const bp: [number, number, number, number][] = [
    [0,    12.0,   0,   50],
    [12.1, 35.4,  51,  100],
    [35.5, 55.4, 101,  150],
    [55.5, 150.4,151,  200],
    [150.5,250.4,201,  300],
    [250.5,500.4,301,  500],
  ];
  for (const [cLo, cHi, iLo, iHi] of bp) {
    if (pm <= cHi) return Math.round(((iHi - iLo) / (cHi - cLo)) * (pm - cLo) + iLo);
  }
  return 500;
}


// City-report density heatmap — severity-weighted so a single critical
// report shows up as a hot spot even with few data points.
const CR_SEVERITY_WEIGHT: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 5 };

export function cityReportHeatmapLayer(reports: IncidentFeature[]) {
  const data = reports
    .filter((r) => r.status !== "resolved")
    .map((r) => ({ lng: r.lng, lat: r.lat, w: CR_SEVERITY_WEIGHT[r.severity] ?? 1 }));

  return new HeatmapLayer<{ lng: number; lat: number; w: number }>({
    id: "incidents-city-heatmap",
    data,
    getPosition: (d) => [d.lng, d.lat],
    getWeight: (d) => d.w,
    radiusPixels: 60,
    intensity: 2.5,
    threshold: 0.08,
    colorRange: [
      [253, 246, 227,  80], // pale cream — very sparse
      [197, 165, 110, 140],
      [162, 58, 38, 180],
      [239,  68,  68, 210], // red
      [185,  28,  28, 235], // deep red
      [127,   0,   0, 255], // maroon — hotspot
    ],
    colorDomain: [0, 20],
    pickable: false,
  });
}

/**
 * Campus CCTV.
 *
 * This is a campus view, not a city view, so the layer draws what an operator
 * actually acts on: the live street frame, not an anonymous dot. A grey pin
 * says "there is a camera"; a thumbnail says "the queue at Chula 12 is moving",
 * which is the thing worth looking at during an incident.
 *
 * The frame is requested from our own /api/cctv/snapshot/:id rather than the
 * vendor host, because deck.gl fetches icons and the page's connect-src does
 * not allow that origin — an IconLayer pointed straight at the vendor draws
 * nothing at all, silently.
 *
 * The dot layer stays underneath as the anchor: it renders instantly, and it is
 * what remains if a frame is missing or the upstream is down.
 */
export function cctvLayer(cameras: CctvCamera[]) {
  return new ScatterplotLayer<CctvCamera>({
    id: "cctv-cameras",
    data: cameras,
    getPosition: (c) => [c.lng, c.lat],
    getRadius: 24,
    radiusMinPixels: 4,
    radiusMaxPixels: 8,
    getFillColor: [229, 231, 235, 220],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
  });
}

/** Live still frames, only once the operator has zoomed in to the campus. */
export function cctvFrameLayer(cameras: CctvCamera[], zoom: number, apiBase: string) {
  // At district zoom six 320×240 frames would cover the map in thumbnails and
  // hide the streets underneath. They earn their space at campus scale.
  const CAMPUS_FRAME_ZOOM = 16;
  if (zoom < CAMPUS_FRAME_ZOOM) return null;

  const withFrame = cameras.filter((c) => c.imageUrl);
  if (withFrame.length === 0) return null;

  return new IconLayer<CctvCamera>({
    id: "cctv-frames",
    data: withFrame,
    getPosition: (c) => [c.lng, c.lat],
    // An OBJECT, never a bare string. deck.gl 9.3 auto-packs icons and reads
    // `icon.url` off the returned value (icon-manager: `if (!icon.url) throw
    // new Error('Icon url is missing.')`). A string has no `.url`, so the
    // accessor throws during updateState and the layer silently draws nothing
    // — no console error, no network request, the map just has no frames.
    // Uniform 4:3 thumbnail slots; required by deck.gl 9 auto-packing types.
    getIcon: (c) => ({ url: `${apiBase}/api/cctv/snapshot/${encodeURIComponent(c.id)}`, width: 320, height: 240 }),
    // Width in pixels; the uniform 4:3 atlas slot
    // lands at 72×54 rather than being stretched to a square.
    sizeUnits: "pixels",
    getSize: () => 72,
    billboard: true,
    // A frame hidden behind an extruded building is no use to an operator —
    // the default view is 3D, so without this the icon is depth-tested against
    // the campus massing and simply vanishes. The dot layer below stays depth
    // tested and keeps its ground position.
    parameters: { depthCompare: "always", depthWriteEnabled: false },
    pickable: false,
  });
}

// H4 fix: removed the dead `export { IconLayer }` re-export (zero
// consumers) and the unused `PolygonLayer` import. SolidPolygonLayer
// (imported above) is the type the rest of this file actually uses.

// ─── Surrounding buildings (urban fabric ~1km around campus) ─────────────
export interface SurroundingBuildingProperties {
  id: string;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  building: string;
  levels: number | null;
  height: number | null;
  operator: string | null;
}

function surroundingBuildingHeightMeters(props: SurroundingBuildingProperties): number {
  if (props.height) return props.height;
  if (props.levels) return props.levels * 3.2;
  return 12;
}


// ─── Flood-prone areas ───────────────────────────────────────────────────
export interface FloodAreaProperties {
  id: string;
  nameTh: string;
  nameEn: string;
  risk: "low" | "medium" | "high";
  cause: string;
  frequency: string;
  lastMajor: string;
}

const FLOOD_COLORS: Record<FloodAreaProperties["risk"], [number, number, number, number]> = {
  low:    [148, 163, 184, 70],
  medium: [162, 58, 38, 90],
  high:   [239, 68, 68, 110],
};


// ─── FloodDash gauge stations near campus ────────────────────────────────
// Live HII gauges relayed by the FloodDash sister system (:8340).
// Water-level stations are colored by HII situation level (1 drought …
// 5 overbank); rain gauges render smaller in flat blue, scaled by 24h mm.

const FLOOD_STATION_LEVEL_COLORS: Record<number, [number, number, number]> = {
  1: [125, 138, 153], // drought — neutral grey
  2: [125, 138, 153], // low — neutral grey
  3: [34, 197, 94],   // normal — green
  4: [245, 158, 11],  // high — amber
  5: [220, 38, 38],   // overbank — red
};

const FLOOD_RAIN_COLOR: [number, number, number] = [56, 189, 248];

export function floodStationsLayer(stations: CampusFloodStation[]) {
  return new ScatterplotLayer<CampusFloodStation>({
    id: "flood-gauges",
    data: stations,
    getPosition: (s) => [s.lng, s.lat],
    getRadius: (s) => {
      if (s.kind === "rain") return 40 + Math.min(80, (s.rain24h ?? 0) * 2);
      return 90;
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 16,
    getFillColor: (s) => {
      if (s.kind === "rain") return [...FLOOD_RAIN_COLOR, 200] as [number, number, number, number];
      const c = FLOOD_STATION_LEVEL_COLORS[s.situationLevel ?? 0] ?? FLOOD_STATION_LEVEL_COLORS[2];
      return [...c, 230] as [number, number, number, number];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 230],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

// ─── Utility layers ──────────────────────────────────────────────────────
// Electricity, water mains, storm drainage, WiFi survey. Geometry comes from
// hand-authored GeoJSONs at /geo/cu-electricity|water|drainage|wifi.geojson —
// see those files for sourcing notes (real substation names verified against
// the CU-MEA SMART CITY agreement; everything else is realistic
// approximation along the road network).

interface ElectricityProps {
  id: string;
  kind: "substation" | "ring-feeder" | "delivery" | "hv-backbone" | "mv-feeder" | "solar-pv" | "battery-storage";
  name: string;
  voltage?: number;
  capacityMva?: number;
  capacityKw?: number;
  capacityMwh?: number;
  status?: string;
  describe?: string;
}

export function electricityLineLayer(collection: FeatureCollection) {
  // Only LineString features (HV backbone, MV feeder).
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-electricity-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: ((f: Feature<LineString, ElectricityProps>) =>
      f.properties.kind === "hv-backbone"
        ? ([31, 110, 67, 240] as [number, number, number, number]) // accent HV
        : ([253, 186, 116, 215] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, ElectricityProps>) =>
      f.properties.kind === "hv-backbone" ? 5 : 2.5) as unknown as number,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 6,
  });
}

export function electricityNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-electricity-nodes",
    data: points,
    getPosition: ((f: Feature<Point, ElectricityProps>) =>
      f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, ElectricityProps>) => {
      const k = f.properties.kind;
      if (k === "substation") return 90;
      if (k === "delivery") return 60;
      if (k === "battery-storage") return 50;
      if (k === "solar-pv") return 45;
      return 35;
    }) as unknown as number,
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    getFillColor: ((f: Feature<Point, ElectricityProps>) => {
      const k = f.properties.kind;
      if (k === "substation") return [31, 110, 67, 240]; // accent
      if (k === "delivery") return [82, 158, 118, 220];
      if (k === "battery-storage") return [167, 139, 250, 230]; // violet
      if (k === "solar-pv") return [250, 204, 21, 230]; // yellow
      return [253, 186, 116, 220];
    }) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

interface WaterProps {
  id: string;
  kind: "supply-point" | "main" | "lateral" | "fire-hydrant";
  diameter?: number;
  status?: string;
  name?: string;
}

export function waterLineLayer(collection: FeatureCollection) {
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-water-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: ((f: Feature<LineString, WaterProps>) =>
      f.properties.kind === "main"
        ? ([56, 189, 248, 230] as [number, number, number, number]) // cyan main
        : ([147, 197, 253, 200] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, WaterProps>) =>
      f.properties.kind === "main" ? 4 : 2) as unknown as number,
    lineWidthMinPixels: 1.5,
    lineWidthMaxPixels: 5,
  });
}

export function waterNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-water-nodes",
    data: points,
    getPosition: ((f: Feature<Point, WaterProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 40,
    radiusMinPixels: 4,
    radiusMaxPixels: 10,
    getFillColor: ((f: Feature<Point, WaterProps>) =>
      f.properties.kind === "supply-point"
        ? ([56, 189, 248, 240] as [number, number, number, number])
        : ([147, 197, 253, 230] as [number, number, number, number])) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

interface DrainageProps {
  id: string;
  kind: "main" | "feeder" | "retention-basin" | "outfall" | "pump-station";
  diameter?: number;
  capacityM3?: number;
  flow?: string;
  status?: string;
  name?: string;
}

export function drainageLineLayer(collection: FeatureCollection) {
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-drainage-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    getLineColor: ((f: Feature<LineString, DrainageProps>) =>
      f.properties.kind === "main"
        ? ([16, 185, 129, 230] as [number, number, number, number]) // emerald main
        : ([110, 231, 183, 200] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, DrainageProps>) =>
      f.properties.kind === "main" ? 5 : 2.5) as unknown as number,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 6,
  });
}

export function drainageNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-drainage-nodes",
    data: points,
    getPosition: ((f: Feature<Point, DrainageProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, DrainageProps>) =>
      f.properties.kind === "retention-basin" ? 110 : 50) as unknown as number,
    radiusMinPixels: 5,
    radiusMaxPixels: 16,
    getFillColor: ((f: Feature<Point, DrainageProps>) => {
      const k = f.properties.kind;
      if (k === "retention-basin") return [16, 185, 129, 220];
      if (k === "outfall") return [56, 189, 248, 230];
      if (k === "pump-station") return [110, 231, 183, 230];
      return [16, 185, 129, 220];
    }) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

// ─── WiFi survey heatmap + points ────────────────────────────────────────

interface WifiProps {
  id: string;
  name: string;
  mbps: number;
  rttMs?: number;
  ssid?: string;
  source?: string;
}


/** NASA GIBS WMTS tile layer — free, no API key. */
/**
 * NASA GIBS global tile layer (MODIS true-color, NDVI, LST, flood).
 * The source product is GoogleMapsCompatible_Level9 — beyond zoom 9 the same
 * tile is stretched to multiple zoom levels, producing useless smears at
 * city scale. `extent` is left global but `currentZoom` gates the visible
 * prop so the layer simply doesn't render at zoom ≥ 10 by default.
 */
// GIBS product → (format, max-level) heuristic. RGB / true-color products
// ship as JPG at Level 9; thematic palettes (NDVI, LST, AOD, NO2, IMERG)
// ship as PNG at lower max levels.
function inferFormat(productId: string): "jpg" | "png" {
  if (productId.includes("CorrectedReflectance")) return "jpg";
  if (productId.includes("TrueColor")) return "jpg";
  if (productId.includes("DayNightBand")) return "png";
  return "png";
}
function inferLevel(productId: string): 6 | 7 | 8 | 9 {
  if (productId.includes("IMERG")) return 6;
  if (productId.includes("OMI_") || productId.includes("AOD")) return 6;
  if (productId.includes("Land_Surface_Temp")) return 7;
  if (productId.includes("DayNightBand")) return 8;
  return 9;
}

interface GibsOpts {
  /** "jpg" for true-color / RGB products, "png" for thematic / index products. */
  format?: "jpg" | "png";
  /** Tile matrix max level: 9 for high-res, 7-8 for thematic, 6 for IMERG / OMI. */
  level?: 6 | 7 | 8 | 9;
}


// ─── BTS / MRT line geometry ────────────────────────────────────────────
// Polyline tracks for the elevated/underground rail network around the
// campus. Coloured per system: BTS Sukhumvit/Silom (greens), MRT Blue
// (blue), Airport/Gold lines (warm). Lines render below station scatter.

// Transit lines render with vertical truth — BTS / ART (Gold APM) at +12 m
// elevated viaduct height, MRT underground at -8 m tunnel depth. The z is
// always set; the 2D top-down view ignores it, but the moment you tilt to
// 3D the network separates into actual elevated rail / surface street /
// subway. PathLayer so we can pass [lng, lat, z] tuples.
export function transitLinesLayer(
  collection: FeatureCollection<LineString, TransitLineProps>,
) {
  const ELEVATED = 12;      // m — BTS / Gold APM viaducts run ~12 m above grade
  const UNDERGROUND = -8;   // m — MRT Blue tunnels run ~8 m below grade

  // Pre-compute paths with z component per feature based on system.
  type TransitPathDatum = {
    path: [number, number, number][];
    color: [number, number, number, number];
    system: "BTS" | "MRT" | "ART";
    width: number;
    properties: TransitLineProps;
  };
  const data: TransitPathDatum[] = [];
  for (const f of collection.features) {
    if (f.geometry.type !== "LineString") continue;
    const sys = f.properties.system;
    const z = sys === "MRT" ? UNDERGROUND : ELEVATED;
    const path = (f.geometry.coordinates as [number, number][]).map(
      (c) => [c[0], c[1], z] as [number, number, number],
    );
    const rgb = hexToRgb(f.properties.color);
    data.push({
      path,
      color: [rgb[0], rgb[1], rgb[2], sys === "MRT" ? 200 : 235],
      system: sys,
      width: sys === "MRT" ? 4 : 5,
      properties: f.properties,
    });
  }

  return new PathLayer<TransitPathDatum>({
    id: "transit-lines",
    data,
    pickable: true,
    getPath: (d) => d.path,
    getColor: (d) => d.color,
    getWidth: (d) => d.width,
    widthUnits: "pixels",
    widthMinPixels: 2,
    widthMaxPixels: 7,
    capRounded: true,
    jointRounded: true,
  });
}

// ─── Campus gates / entrances ───────────────────────────────────────────
// Every barrier=gate / barrier=lift_gate / entrance=* node on or near the
// campus perimeter. Named gates (ประตูพญาไท 1/2/3, อังรีดูนังต์ 1/2, ประตูดำ)
// render larger and amber; unnamed minor gates stay small and grey.

export function campusGatesLayer(collection: FeatureCollection<Point, GateProps>) {
  return new ScatterplotLayer({
    id: "campus-gates",
    data: collection.features,
    getPosition: ((f: Feature<Point, GateProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, GateProps>) => (f.properties.named ? 36 : 18)) as unknown as number,
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
    getFillColor: ((f: Feature<Point, GateProps>) =>
      f.properties.named
        ? [31, 110, 67, 240]
        : [148, 163, 184, 220]) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
  });
}

// ─── Road network — classified by priority ──────────────────────────────
// Renders the OSM road network as a visible map element. Width + colour
// scale by `priority`: motorway/primary = thick warm, secondary = medium
// cyan, tertiary = thin cyan, residential/lane = thinnest neutral.

const ROAD_STYLE: Record<number, { color: [number, number, number]; width: number }> = {
  6: { color: [251, 113, 133], width: 4.5 },  // motorway
  5: { color: [251, 146, 60],  width: 3.5 },  // primary / secondary
  4: { color: [125, 211, 252], width: 2.5 },  // tertiary
  3: { color: [148, 163, 184], width: 1.5 },  // residential / lane
  2: { color: [148, 163, 184], width: 1.0 },  // unclassified / minor
};


// ─── Urban fabric (every OSM building in the expanded bbox) ────────────
// Background context — the residential blocks, shophouses, low-rises that
// fill the gaps between the named towers. ~24k polygons, height from OSM
// `building:levels` × 3 m when available, otherwise a per-tag heuristic.
//
// Distinct dim palette so the curated neighborhood-buildings + campus
// buildings stay visually dominant. Default off; turning it on gives a full
// 3D urban-fabric model around Chula.

export interface UrbanFabricProps {
  id: string;
  name: string | null;
  building: string;
  height: number;
  levels: string | null;
  heightSource: "osm" | "heuristic";
}


// ═══════════════════════════════════════════════════════════════════════════
// PRATUMWAN X-RAY — district-wide subsurface + overhead infrastructure
// ═══════════════════════════════════════════════════════════════════════════
//
// Every layer here renders a real-world piece of Bangkok civil engineering at
// approximately correct geometry and depth. See scripts/generate-pratumwan-geo.mjs
// for the source data and apps/web/src/lib/energyModel.ts for the EUI numbers.
// Depths in meters below grade. Z=0 is street level; positive Z is above-grade
// (overhead poles, building extrusions).

interface PratumwanLineProps {
  id: string;
  name?: string;
  kind?: string;
  diameter?: number;
  material?: string;
  depthM?: number;
  voltage?: number;
  flowDir?: string;
  describe?: string;
}

// Generic builder — extracts LineString features at a given depth, returns
// path-with-properties tuples ready for PathLayer.
function pratumwanLinesAtDepth(
  collection: FeatureCollection,
  fallbackDepthM: number,
): Array<{ path: [number, number, number][]; properties: PratumwanLineProps }> {
  const out: Array<{ path: [number, number, number][]; properties: PratumwanLineProps }> = [];
  for (const f of collection.features) {
    if (f.geometry.type !== "LineString") continue;
    const props = (f.properties ?? {}) as PratumwanLineProps;
    const z = typeof props.depthM === "number" ? props.depthM : -fallbackDepthM;
    const coords = (f.geometry.coordinates as [number, number][] | [number, number, number][]).map(
      (c) => (c.length === 3
        ? ([c[0], c[1], (c as [number, number, number])[2]] as [number, number, number])
        : ([c[0], c[1], z] as [number, number, number])),
    );
    out.push({ path: coords, properties: props });
  }
  return out;
}

// ─── Schematic underground helper — SimCity-style hairlines, no halos ───────
function schematicPath(
  id: string,
  paths: { path: [number, number, number][]; properties: PratumwanLineProps }[],
  rgba: [number, number, number, number],
  widthPx = 2,
): Layer {
  return new PathLayer({
    id,
    data: paths,
    getPath: (d) => d.path,
    getColor: rgba,
    getWidth: widthPx,
    widthUnits: "pixels",
    widthMinPixels: 2,
    widthMaxPixels: 4,
    pickable: true,
  });
}

// ─── Pratumwan X-Ray palette — one RGB per system, matching its swatch in
// presets.ts exactly so the layer palette and the map never disagree about
// what color means what. Previously several of these were copy-pasted from
// the wrong layer (boundary and MEA-HV both silently rendered in storm's
// green instead of their own declared amber), and storm's green collided
// with the campus electricity layer's green — four unrelated systems
// reading as the same color. Keep this the only place these RGBs are typed.
export const PRATUMWAN_COLOR = {
  boundary: [251, 191, 36] as [number, number, number],   // #FBBF24 amber
  metro: [80, 100, 120] as [number, number, number],       // #506478 slate
  water: [56, 120, 160] as [number, number, number],       // #3878A0 blue
  sewer: [111, 100, 90] as [number, number, number],       // #6F645A brown
  storm: [14, 116, 144] as [number, number, number],       // #0E7490 teal — was storm's own green, which collided with campus electricity's [31,110,67]
  stormCulvert: [92, 163, 175] as [number, number, number], // paler tint of storm's teal — a culvert is a covered storm drain, not a sewer
  meaHv: [161, 98, 7] as [number, number, number],         // #A16207 amber-brown
  meaLv: [201, 162, 39] as [number, number, number],       // #C9A227 muted gold — lighter kin of meaHv (LV vs HV), was wrongly sharing sewer's exact brown
};

// ─── 1. District boundary — translucent outline ────────────────────────────
export function pratumwanBoundaryLayer(collection: FeatureCollection): Layer {
  const [r, g, b] = PRATUMWAN_COLOR.boundary;
  return new GeoJsonLayer({
    id: "pratumwan-boundary",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: false,
    getFillColor: [r, g, b, 10],
    getLineColor: [r, g, b, 200],
    getLineWidth: 2,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 3,
  });
}

// ─── 2. MRT Blue Line — schematic tunnel trace (known MRT alignment) ───────
export function pratumwanMetroTunnelLayer(collection: FeatureCollection): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.metro;
  const paths = pratumwanLinesAtDepth(collection, 22);
  return [schematicPath("pratumwan-metro-tunnel", paths, [r, g, b, 230], 3)];
}

// ─── 3. MRT stations — flat footprints (known station locations) ───────────
export interface StationFeatureProps {
  id: string;
  code: string;
  name: string;
  nameTh: string;
  line: string;
  topZ: number;
  bottomZ: number;
  lengthM: number;
  widthM: number;
  construction: string;
}

export function pratumwanMetroStationsLayer(
  collection: FeatureCollection<Polygon, StationFeatureProps>,
): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.metro;
  return [
    new GeoJsonLayer({
      id: "pratumwan-metro-stations",
      data: collection as unknown as FeatureCollection,
      stroked: true,
      filled: true,
      pickable: true,
      getFillColor: [r, g, b, 40],
      getLineColor: [r, g, b, 210],
      getLineWidth: 2,
      lineWidthUnits: "pixels",
      lineWidthMinPixels: 1,
      lineWidthMaxPixels: 3,
    }),
  ];
}

function centroidOf(ring: [number, number][]): [number, number] {
  let sx = 0, sy = 0, n = 0;
  for (let i = 0; i < ring.length - 1; i++) { sx += ring[i][0]; sy += ring[i][1]; n++; }
  return [sx / n, sy / n];
}

// ─── 4. MWA water mains — schematic (approx routing, MWA network) ──────────
export function pratumwanWaterLayer(collection: FeatureCollection): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.water;
  const paths = pratumwanLinesAtDepth(collection, 1.5);
  return [schematicPath("pratumwan-water", paths, [r, g, b, 220], 2)];
}

// ─── 5. BMA combined sewers — schematic (approx BMA routing) ─────────────
export function pratumwanSewerLayer(collection: FeatureCollection): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.sewer;
  const paths = pratumwanLinesAtDepth(collection, 3);
  return [schematicPath("pratumwan-sewer", paths, [r, g, b, 210], 2)];
}

// ─── 6. Storm drainage — khlongs (open) + culverts (schematic) ─────────────
// Culverts render as a paler tint of the same teal, not sewer's brown — a
// culvert is a covered storm drain, not a sewer, and sharing sewer's exact
// color made the two indistinguishable.
export function pratumwanStormLayer(collection: FeatureCollection): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.storm;
  const [cr, cg, cb] = PRATUMWAN_COLOR.stormCulvert;
  const culverts = pratumwanLinesAtDepth(
    { type: "FeatureCollection", features: collection.features.filter((f) =>
      f.geometry.type === "LineString" && (f.properties as PratumwanLineProps).kind !== "khlong"
    )} as FeatureCollection,
    4,
  );
  const khlongs = pratumwanLinesAtDepth(
    { type: "FeatureCollection", features: collection.features.filter((f) =>
      f.geometry.type === "LineString" && (f.properties as PratumwanLineProps).kind === "khlong"
    )} as FeatureCollection,
    2,
  );
  return [
    schematicPath("pratumwan-storm-khlongs", khlongs, [r, g, b, 200], 3),
    schematicPath("pratumwan-storm-culverts", culverts, [cr, cg, cb, 180], 2),
  ];
}

// ─── 7. MEA underground HV — schematic cable trace (approx) ──────────────
interface MeaSubstationProps {
  id: string;
  kind: "substation";
  name: string;
  nameTh: string;
  voltage: number;
  capacityMva: number;
  widthM: number;
  lengthM: number;
  depthM: number;
}

export function pratumwanMeaHvLayer(collection: FeatureCollection): Layer[] {
  const [r, g, b] = PRATUMWAN_COLOR.meaHv;
  const paths = pratumwanLinesAtDepth(collection, 1.2);
  return [schematicPath("pratumwan-mea-hv", paths, [r, g, b, 190], 2)];
}


// ─── 9. Landmarks — labeled key buildings ──────────────────────────────────
export interface PratumwanLandmarkProps {
  id: string;
  name: string;
  nameTh: string;
  category: "mall" | "office" | "hotel" | "hospital" | "condo" | "university";
  heightM: number;
  gfaM2: number;
  euiKwhM2Yr: number;
  hasSTP: boolean;
  operator: string;
  describe?: string;
}

const CATEGORY_COLOR: Record<PratumwanLandmarkProps["category"], [number, number, number]> = {
  mall:       [232, 121, 249],
  office:     [125, 211, 252],
  hotel:      [108, 99, 92],
  hospital:   [239, 68, 68],
  condo:      [167, 139, 250],
  university: [52, 211, 153],
};

// All 18 landmarks get a dot; only the tallest get a permanent floating
// label. At district scale, 18 always-on text boxes piled into overlapping
// clutter — this was the single biggest source of "the underground layer
// is incomprehensible." Shorter landmarks are still visible (colored by
// category) and still real data, just not permanently labeled at this zoom.
const LANDMARK_LABEL_MIN_HEIGHT_M = 60;


// ─── Ground plane — translucent overlay to read subsurface clearly ─────────
// Renders a flat polygon at Z=0 with low alpha. Calling site decides bbox.
export function pratumwanGroundPlane(bbox: [number, number, number, number]): Layer {
  const [w, s, e, n] = bbox;
  return new SolidPolygonLayer({
    id: "pratumwan-ground-plane",
    data: [{
      polygon: [[w, s, 0], [e, s, 0], [e, n, 0], [w, n, 0], [w, s, 0]] as [number, number, number][],
    }],
    getPolygon: (d) => d.polygon,
    getFillColor: [231, 229, 221, 50],
    getLineColor: [0, 0, 0, 0],
    extruded: false,
    pickable: false,
  });
}

function colorForEui(eui: number): [number, number, number, number] {
  // 0 → blue, 100 → cyan, 200 → yellow, 350 → orange, 450 → red
  if (eui < 50) return [56, 189, 248, 210];
  if (eui < 130) return [125, 211, 252, 220];
  if (eui < 200) return [250, 204, 21, 225];
  if (eui < 290) return [251, 146, 60, 235];
  if (eui < 380) return [239, 68, 68, 240];
  return [185, 28, 28, 245];
}


// ═══════════════════════════════════════════════════════════════════════════
// OPEN-DATA MOBILITY — real OSM extractions (© OpenStreetMap contributors,
// ODbL 1.0). Geometry only: bus route linework, stop nodes, micromobility
// inventory, mapped parking. NONE of these are live vehicle/occupancy feeds —
// describe strings in presets.ts say so explicitly.
//
// Colors follow the AXIOM data grammar: generic infrastructure = warm data
// grey (var(--data) #6F6C63), cycling = the one accent green (var(--accent)
// #1F6E43 + its soft step), parking = the service-slate already used for
// campus service zones. deck.gl needs numeric RGB, so the token values are
// inlined here — layers.ts is "the data" layer of the design system.
// ═══════════════════════════════════════════════════════════════════════════

const OM_BUS_GREY: [number, number, number] = [111, 108, 99];      // var(--data)
const OM_CYCLE_GREEN: [number, number, number] = [31, 110, 67];    // var(--accent)
const OM_CYCLE_SOFT: [number, number, number] = [82, 158, 118];    // soft accent step
const OM_PARKING_SLATE: [number, number, number] = [122, 132, 151]; // service slate
const OM_POINT_STROKE: [number, number, number, number] = [10, 14, 20, 230];


export interface CityBusStopProps {
  name: string | null;
  nameTh: string | null;
  shelter: string | null;
  routeRefs: string[] | null;
  osmId: string;
}


export type MicromobilityKind =
  | "bicycle-parking"
  | "bicycle-rental"
  | "cycleway"
  | "footway-shared";

export interface MicromobilityProps {
  kind: MicromobilityKind;
  name?: string | null;
  operator?: string | null;
  /** Rack capacity — only when OSM tagged it. */
  capacity?: number | null;
  covered?: string | null;
  fee?: string | null;
  surface?: string | null;
  osmId: string;
}


export interface DensityBin {
  /** Upper GFA bound (m², exclusive). Last bin is Infinity. */
  maxGfaM2: number;
  color: [number, number, number];
  label: string;
}


type DensityFeature = Feature<Polygon | MultiPolygon, BuildingProperties & { gfaM2: number }>;


// ═══════════════════════════════════════════════════════════════════════════
// Operations layers — modeled from real campus geometry (scenario tier).
// Static attributes come from public/geo/ops/*.geojson (scripts/build-ops-
// mock.mjs); the clock-driven values come from lib/opsModel.ts at render
// time, keyed on the minute so nothing flickers between frames.
// ═══════════════════════════════════════════════════════════════════════════

import {
  binFillNow, internalSpeedNow, occupiedNow, parkingOccupiedNow, type BuildingUse,
} from "../lib/opsModel";

export interface OpsProps { provenance: "modeled"; id: string }
export interface ParkingCapacityProps extends OpsProps {
  name: string | null; kind: "surface" | "structure"; levels: number; areaM2: number;
  capacity: number; capacitySource: "osm-tag" | "area-model"; operator: string | null; fee: string | null;
}
export interface OccupancyProps extends OpsProps {
  name: string; nameTh: string | null; levels: number; footprintM2: number; gfaM2: number; capacity: number; use: BuildingUse;
}
export interface InternalTrafficProps extends OpsProps {
  name: string | null; highway: string; lengthM: number; gateApproach: boolean; freeFlowKmh: number;
}
export interface WifiSignalProps extends OpsProps {
  building: string; buildingId: string; accessPoints: number; signalDbm: number; mbps: number;
  nearestSurvey: string; surveyDistM: number; ssid: string;
}
export interface FireHoseProps extends OpsProps {
  kind: "hose-cabinet" | "hydrant"; building: string | null; buildingId: string | null; floors: string | null;
  gate?: string; pressureBar: number; lastInspected: string; status: "ok" | "due" | "overdue";
}
export interface TrashProps extends OpsProps {
  site: string; binType: "general" | "recycling" | "food-waste"; litres: number;
}

/** Live-derived fields are attached once per minute so tooltips and colours agree. */
export interface OpsClock { now: Date; minute: number; tempo?: number; parkingCurvePct?: number }

const OPS_MAGENTA: [number, number, number] = [183, 48, 122];   // campus identity (var(--brand))
const OPS_SLATE: [number, number, number] = [122, 132, 151];
const OPS_GREEN: [number, number, number] = [31, 110, 67];
const OPS_AMBER: [number, number, number] = [197, 165, 110];
const OPS_BRICK: [number, number, number] = [162, 58, 38];
const OPS_GREY: [number, number, number] = [111, 108, 99];

function fillRamp(pct: number): [number, number, number, number] {
  // 0 % → quiet slate, 60 % → amber, 90 %+ → brick
  if (pct >= 90) return [...OPS_BRICK, 200];
  if (pct >= 60) return [...OPS_AMBER, 190];
  return [...OPS_SLATE, 150];
}

// ── 1. Parking capacity ──────────────────────────────────────────────────────
/** The largest lots carry a counter; the rest read from colour. Forty counters
 *  each prefixed "SIM" turned the campus into a wall of tags that buried the
 *  buildings, buses and alerts the operator opened the map for. */
export const MAX_PARKING_LABELS = 10;
/** SIM stays on every counter that is drawn: a modelled number on the map must
 *  never be readable as a live count. Fewer counters, not unmarked ones. */
export function parkingLabelText(occupied: number, capacity: number): string {
  return `SIM ${occupied} / ${capacity}`;
}

export function parkingCapacityLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, ParkingCapacityProps>,
  clock: OpsClock,
  view?: { width: number; height: number; longitude: number; latitude: number; zoom: number; pitch: number; bearing: number; obstacles?: LabelBox[] },
): Layer[] {
  type PF = Feature<Polygon | MultiPolygon, ParkingCapacityProps & { occupied: number; pct: number }>;
  const curve = clock.parkingCurvePct ?? 50;
  const data = collection.features.map((f) => {
    const occupied = parkingOccupiedNow(f.properties.id, f.properties.capacity, curve, clock.now);
    return { ...f, properties: { ...f.properties, occupied, pct: Math.round((occupied / f.properties.capacity) * 100) } } as PF;
  });
  const candidates = data.filter((f) => f.properties.capacity >= 40);
  const viewport = view ? new WebMercatorViewport(view) : null;
  const labels = viewport && view ? chooseParkingLabels(candidates.map(feature => ({
    id: feature.properties.id, capacity: feature.properties.capacity,
    text: parkingLabelText(feature.properties.occupied, feature.properties.capacity),
    position: viewport.project(polygonCentroid(feature.geometry)).slice(0, 2) as [number, number],
    feature,
  })), view, view.obstacles, MAX_PARKING_LABELS).map(candidate => candidate.feature) : candidates.slice(0, MAX_PARKING_LABELS);
  return [
    new GeoJsonLayer({
      id: "parking-capacity",
      data: { type: "FeatureCollection", features: data } as FeatureCollection,
      stroked: true, filled: true, pickable: true,
      getFillColor: ((f: PF) => fillRamp(f.properties.pct)) as unknown as [number, number, number, number],
      getLineColor: [OPS_SLATE[0], OPS_SLATE[1], OPS_SLATE[2], 230],
      getLineWidth: 1, lineWidthUnits: "pixels", lineWidthMinPixels: 0.5, lineWidthMaxPixels: 2,
      updateTriggers: { getFillColor: clock.minute },
    }),
    new TextLayer<PF>({
      id: "parking-capacity-labels",
      data: labels,
      getPosition: (f) => polygonCentroid(f.geometry),
      getText: (f) => parkingLabelText(f.properties.occupied, f.properties.capacity),
      characterSet: "auto",
      getSize: 11, sizeUnits: "pixels",
      getColor: [253, 246, 227, 240],
      background: true, getBackgroundColor: [12, 16, 26, 200], backgroundPadding: [4, 2, 4, 2],
      fontFamily: '"Jost", "Prompt", sans-serif', fontWeight: "600",
      billboard: true, pickable: false,
      parameters: { depthCompare: "always", depthWriteEnabled: false },
      updateTriggers: { getText: clock.minute },
    }),
  ];
}

function polygonCentroid(g: Polygon | MultiPolygon): [number, number] {
  const ring = g.type === "Polygon" ? g.coordinates[0] : g.coordinates[0][0];
  let x = 0, y = 0, n = 0;
  for (let i = 0; i < ring.length - 1; i++) { x += ring[i][0]; y += ring[i][1]; n++; }
  return n ? [x / n, y / n] : [ring[0][0], ring[0][1]];
}

// ── 2. Building occupancy ────────────────────────────────────────────────────
export function buildingOccupancyLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, OccupancyProps>,
  clock: OpsClock,
  opts: { extruded: boolean },
): Layer {
  type OF = Feature<Polygon | MultiPolygon, OccupancyProps & { occupied: number; pct: number }>;
  const data = collection.features.map((f) => {
    const occupied = occupiedNow(f.properties.id, f.properties.capacity, f.properties.use, clock.now, clock.tempo ?? 1);
    return { ...f, properties: { ...f.properties, occupied, pct: Math.round((occupied / f.properties.capacity) * 100) } } as OF;
  });
  return new GeoJsonLayer({
    id: "building-occupancy",
    data: { type: "FeatureCollection", features: data } as FeatureCollection,
    stroked: true, filled: true, pickable: true,
    extruded: opts.extruded,
    getElevation: ((f: OF) => Math.max(3, f.properties.levels * 3.2)) as unknown as number,
    getFillColor: ((f: OF) => {
      // Magenta identity ramp: alpha tracks fullness so an empty building reads as an outline.
      const a = 40 + Math.round(f.properties.pct * 1.9);
      return [OPS_MAGENTA[0], OPS_MAGENTA[1], OPS_MAGENTA[2], Math.min(230, a)];
    }) as unknown as [number, number, number, number],
    getLineColor: [OPS_MAGENTA[0], OPS_MAGENTA[1], OPS_MAGENTA[2], 220],
    getLineWidth: 1, lineWidthUnits: "pixels", lineWidthMinPixels: 0.5,
    updateTriggers: { getFillColor: clock.minute },
  });
}

// ── 3. Internal traffic ──────────────────────────────────────────────────────
export function internalTrafficLayer(
  collection: FeatureCollection<LineString, InternalTrafficProps>,
  clock: OpsClock,
): Layer {
  type TF = Feature<LineString, InternalTrafficProps & { speedKmh: number }>;
  const data = collection.features.map((f) => ({
    ...f,
    properties: { ...f.properties, speedKmh: internalSpeedNow(f.properties.id, f.properties.freeFlowKmh, f.properties.gateApproach, clock.now) },
  })) as TF[];
  return new PathLayer<TF>({
    id: "internal-traffic",
    data,
    getPath: (f) => f.geometry.coordinates as [number, number][],
    getColor: (f) => {
      const ratio = f.properties.speedKmh / f.properties.freeFlowKmh;
      if (ratio < 0.4) return [OPS_BRICK[0], OPS_BRICK[1], OPS_BRICK[2], 230];
      if (ratio < 0.7) return [OPS_AMBER[0], OPS_AMBER[1], OPS_AMBER[2], 230];
      return [OPS_GREEN[0], OPS_GREEN[1], OPS_GREEN[2], 220];
    },
    getWidth: (f) => (f.properties.gateApproach ? 4 : 3),
    widthUnits: "pixels", widthMinPixels: 2, widthMaxPixels: 6,
    capRounded: true, jointRounded: true,
    pickable: true,
    updateTriggers: { getColor: clock.minute },
  });
}

// ── 4. WiFi signal ───────────────────────────────────────────────────────────
export function wifiSignalLayer(collection: FeatureCollection<Point, WifiSignalProps>): Layer[] {
  type WF = Feature<Point, WifiSignalProps>;
  return [
    new HeatmapLayer<WF>({
      id: "wifi-signal-heat",
      data: collection.features,
      getPosition: (f) => f.geometry.coordinates as [number, number],
      getWeight: (f) => Math.max(0, (f.properties.signalDbm + 85) / 35), // -85 → 0, -50 → 1
      radiusPixels: 60, intensity: 1, threshold: 0.05, aggregation: "MEAN",
      colorRange: [
        [162, 58, 38, 0], [162, 58, 38, 110], [197, 165, 110, 170], [34, 211, 238, 210], [31, 110, 67, 235],
      ],
    }),
    new ScatterplotLayer<WF>({
      id: "wifi-signal-aps",
      data: collection.features,
      getPosition: (f) => f.geometry.coordinates as [number, number],
      getRadius: (f) => 6 + Math.min(14, f.properties.accessPoints),
      radiusUnits: "meters", radiusMinPixels: 3, radiusMaxPixels: 9,
      getFillColor: (f) => {
        const d = f.properties.signalDbm;
        if (d >= -60) return [31, 110, 67, 230];
        if (d >= -70) return [34, 211, 238, 230];
        if (d >= -78) return [197, 165, 110, 230];
        return [162, 58, 38, 230];
      },
      stroked: true, getLineColor: [10, 14, 20, 230], lineWidthMinPixels: 1,
      pickable: true,
    }),
  ];
}

// ── 5. Fire hose cabinets + hydrants ────────────────────────────────────────
export function fireHoseLayer(collection: FeatureCollection<Point, FireHoseProps>): Layer {
  type FF = Feature<Point, FireHoseProps>;
  return new ScatterplotLayer<FF>({
    id: "fire-hose",
    data: collection.features,
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getRadius: (f) => (f.properties.kind === "hydrant" ? 5 : 2.5),
    radiusUnits: "meters", radiusMinPixels: 2.5, radiusMaxPixels: 8,
    getFillColor: (f) => {
      if (f.properties.status === "overdue") return [OPS_BRICK[0], OPS_BRICK[1], OPS_BRICK[2], 240];
      if (f.properties.status === "due") return [OPS_AMBER[0], OPS_AMBER[1], OPS_AMBER[2], 240];
      return [OPS_GREEN[0], OPS_GREEN[1], OPS_GREEN[2], 200];
    },
    stroked: true, getLineColor: [10, 14, 20, 240], lineWidthMinPixels: 1,
    pickable: true,
  });
}

// ── 6. Trash collection ─────────────────────────────────────────────────────
export function trashCollectionLayer(collection: FeatureCollection<Point, TrashProps>, clock: OpsClock): Layer {
  type TF = Feature<Point, TrashProps & { fillPct: number }>;
  const data = collection.features.map((f) => ({
    ...f,
    properties: { ...f.properties, fillPct: binFillNow(f.properties.id, f.properties.binType, f.properties.site, clock.now) },
  })) as TF[];
  return new ScatterplotLayer<TF>({
    id: "trash-collection",
    data,
    getPosition: (f) => {
      // A gate hosts a general + recycling pair at the same node — nudge the second east so both read.
      const [lng, lat] = f.geometry.coordinates;
      return f.properties.binType === "recycling" ? [lng + 0.00006, lat] : f.properties.binType === "food-waste" ? [lng, lat + 0.00005] : [lng, lat];
    },
    getRadius: (f) => 2 + f.properties.litres / 200,
    radiusUnits: "meters", radiusMinPixels: 3, radiusMaxPixels: 8,
    getFillColor: (f) => {
      const p = f.properties.fillPct;
      if (p >= 85) return [OPS_BRICK[0], OPS_BRICK[1], OPS_BRICK[2], 240];
      if (p >= 60) return [OPS_AMBER[0], OPS_AMBER[1], OPS_AMBER[2], 240];
      return [OPS_GREY[0], OPS_GREY[1], OPS_GREY[2], 220];
    },
    stroked: true, getLineColor: [10, 14, 20, 240], lineWidthMinPixels: 1,
    pickable: true,
    updateTriggers: { getFillColor: clock.minute },
  });
}

export function declutterUndergroundLabels(
  layers: Layer[],
  view: { width: number; height: number; longitude: number; latitude: number; zoom: number; pitch: number; bearing: number },
  obstacles: LabelBox[] = [],
): Layer[] {
  const viewport = new WebMercatorViewport(view);
  const candidates = layers.flatMap(layer => {
    if (!layer.id.endsWith('labels-3ds') || !Array.isArray(layer.props.data)) return [];
    return (layer.props.data as UndergroundLabelDatum[]).map((datum, index) => ({
      id: `${layer.id}:${index}`, capacity: 1, text: datum.text,
      position: viewport.project(datum.position).slice(0, 2) as [number, number], datum, layerId: layer.id,
    }));
  });
  const selected = chooseParkingLabels(candidates, view, obstacles);
  // Always-depth labels still need to draw last: later extrusions can overwrite
  // their colour because label backgrounds deliberately do not write depth.
  return layers.map(layer => layer.id.endsWith('labels-3ds')
    ? layer.clone({ data: selected.filter(candidate => candidate.layerId === layer.id).map(candidate => candidate.datum) })
    : layer).sort((a, b) => Number(a instanceof TextLayer) - Number(b instanceof TextLayer));
}
